import { useState } from "react";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft,
  MapPin,
  Home,
  BedDouble,
  Bath,
  DollarSign,
  Building,
  Tag,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Loader2,
  FileText,
  Mail,
  Phone,
  Check,
  Clock,
} from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useParams, Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import WholesalerListingMap from "@/components/wholesaler-listing-map";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

interface WholesalerListingDetail {
  id: number;
  address: string;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  askingPrice: string;
  arv: string | null;
  bedrooms: number | null;
  bathrooms: string | null;
  sqft: number | null;
  lotSize: string | null;
  yearBuilt: number | null;
  description: string | null;
  photos: string[];
  dealNotes: string | null;
  sellerFinancing: boolean;
  interestRate: string | null;
  termYears: number | null;
  downPaymentPercent: string | null;
  estimatedDaysAvailable: number | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  partnerId: number;
  partnerName: string | null;
  partnerCompanyName: string | null;
  partnerEmail: string | null;
  partnerPhone: string | null;
}

function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null) return "—";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (!isFinite(num) || num === 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
}

function formatPercent(val: number | string | null | undefined): string {
  if (val == null) return "—";
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (!isFinite(num) || num === 0) return "—";
  return `${num}%`;
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null || value === "" || value === 0 || value === "0") return null;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

export default function AppWholesalerListingDetail() {
  const queryClient = useQueryClient();
  usePageTitle("Wholesaler Listing");
  const [, navigate] = useLocation();
  const params = useParams<{ id: string }>();
  const listingId = parseInt(params.id || "0", 10);
  const { toast } = useToast();

  const [showInterest, setShowInterest] = useState(false);
  const [interestSent, setInterestSent] = useState(false);
  const [message, setMessage] = useState("");
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { data: listing, isLoading } = useQuery<WholesalerListingDetail>({
    queryKey: ["/api/app/wholesaler-listings", listingId],
    enabled: listingId > 0,
  });

  const interestMutation = useMutation({
    mutationFn: async (msg: string) => {
      const res = await apiRequest("POST", `/api/app/wholesaler-listings/${listingId}/interested`, { message: msg });
      return res.json();
    },
    onSuccess: () => setInterestSent(true),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Could not send interest";
      toast({ title: msg, variant: "destructive" });
    },
  });

  const { data: copyCheck } = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/underwriting/copy-check", "wholesaler", listingId],
    queryFn: async () => {
      const res = await fetch(`/api/app/underwriting/copy-check?sourceType=wholesaler&sourceListingId=${listingId}`, { credentials: "include" });
      return res.json();
    },
    enabled: listingId > 0,
  });
  const alreadyCopied = copyCheck?.propertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "wholesaler", sourceListingId: listingId });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check", "wholesaler", listingId] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-72 w-full rounded-xl" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!listing) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-12 text-center">
          <Home className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Listing not found</h2>
          <p className="text-gray-500 mb-4">This listing may have been removed or is no longer available.</p>
          <Button asChild data-testid="button-back-to-buy">
            <Link href="/app/buy"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Buy</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const photos = (listing.photos ?? []).filter(Boolean);
  const hasPhotos = photos.length > 0;
  const hasMany = photos.length > 1;
  const safeIdx = Math.min(photoIdx, Math.max(photos.length - 1, 0));
  const goPrev = () => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  const goNext = () => setPhotoIdx((i) => (i + 1) % photos.length);

  const initials = (listing.partnerName || "W")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  const handleCloseInterest = () => {
    setShowInterest(false);
    setInterestSent(false);
    setMessage("");
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        <Link
          href="/app/buy"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors"
          data-testid="button-back-to-buy"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Buy
        </Link>

        <div className="relative rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-slate-100 to-slate-200" style={{ minHeight: "24rem" }}>
          {hasPhotos ? (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="block w-full h-full"
              data-testid="button-open-gallery"
              aria-label="Open photo gallery"
            >
              <img
                src={photos[safeIdx]}
                alt={`${listing.address} photo ${safeIdx + 1}`}
                className="w-full h-72 md:h-96 object-cover"
              />
            </button>
          ) : (
            <div className="w-full h-72 md:h-96 flex items-center justify-center">
              <Home className="h-16 w-16 text-slate-300" />
            </div>
          )}
          {hasMany && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                aria-label="Previous photo"
                data-testid="button-prev-photo"
              >
                <ChevronLeft className="h-5 w-5 text-gray-900" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                aria-label="Next photo"
                data-testid="button-next-photo"
              >
                <ChevronRight className="h-5 w-5 text-gray-900" />
              </button>
              <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium rounded-full px-3 py-1" data-testid="text-photo-count">
                {safeIdx + 1} / {photos.length}
              </div>
            </>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-1.5" data-tour="wholesaler-listing-detail-financing">
            <Badge className="bg-amber-500 text-white border-0 shadow-sm text-xs font-semibold px-2.5 py-1 flex items-center gap-1">
              <Tag className="h-3 w-3" />
              Wholesale
            </Badge>
            {listing.sellerFinancing && (
              <Badge className="bg-green-500 text-white border-0 shadow-sm text-xs font-semibold px-2.5 py-1">
                Seller Financing
              </Badge>
            )}
          </div>
        </div>

        {hasMany && (
          <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="thumbnail-strip">
            {photos.slice(0, 12).map((p, i) => (
              <button
                key={`${p}-${i}`}
                type="button"
                onClick={() => setPhotoIdx(i)}
                className={`flex-shrink-0 h-16 w-24 rounded overflow-hidden border-2 transition-all ${
                  i === safeIdx ? "border-amber-500" : "border-transparent opacity-70 hover:opacity-100"
                }`}
                aria-label={`Show photo ${i + 1}`}
                data-testid={`button-thumb-${i}`}
              >
                <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
            {photos.length > 12 && (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="flex-shrink-0 h-16 w-24 rounded bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 flex items-center justify-center"
                data-testid="button-more-photos"
              >
                +{photos.length - 12}
              </button>
            )}
          </div>
        )}

        <div className="flex items-start justify-between mb-6 gap-4">
          <div data-tour="wholesaler-listing-detail-heading">
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-listing-address">
              {listing.address}
            </h1>
            <p className="text-gray-500 flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {[listing.city, listing.state, listing.zipCode].filter(Boolean).join(", ") || "Location not specified"}
            </p>
            <div className="flex items-baseline gap-4 mt-3">
              <p className="text-3xl font-bold text-gray-900" data-testid="text-listing-price">
                {formatCurrency(listing.askingPrice)}
              </p>
              {listing.arv && (
                <p className="text-sm text-green-700">
                  <span className="text-gray-400">ARV </span>
                  <span className="font-semibold">{formatCurrency(listing.arv)}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
              {listing.bedrooms != null && (
                <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" />{listing.bedrooms} bd</span>
              )}
              {listing.bathrooms != null && (
                <span className="flex items-center gap-1"><Bath className="h-4 w-4" />{listing.bathrooms} ba</span>
              )}
              {listing.sqft != null && (
                <span className="flex items-center gap-1"><Home className="h-4 w-4" />{listing.sqft.toLocaleString()} sqft</span>
              )}
              {listing.estimatedDaysAvailable != null && (
                <span className="flex items-center gap-1 text-amber-700"><Clock className="h-4 w-4" />~{listing.estimatedDaysAvailable} days available</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="wholesaler-listing-detail" />
              <GuideButton pageKey="wholesaler-listing-detail" />
            </div>
            <Button
              onClick={() => setShowInterest(true)}
              data-testid="button-express-interest"
              data-tour="wholesaler-listing-detail-interest"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              I'm Interested
            </Button>
            <Button
              variant={alreadyCopied ? "default" : "outline"}
              onClick={() => {
                if (alreadyCopied && copyCheck?.propertyId) {
                  navigate(`/app/underwriting/${copyCheck.propertyId}`);
                } else {
                  copyMutation.mutate();
                }
              }}
              disabled={copyMutation.isPending}
              data-testid="button-underwrite-myself"
              data-tour="wholesaler-listing-detail-underwrite"
            >
              {copyMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Copying...</>
              ) : alreadyCopied ? (
                "Review in Underwriting"
              ) : (
                "Copy to Underwriting"
              )}
            </Button>
          </div>
        </div>
        <GuideNudgeBanner pageKey="wholesaler-listing-detail" />

        <WholesalerListingMap
          address={listing.address}
          city={listing.city}
          state={listing.state}
          zipCode={listing.zipCode}
          latitude={listing.latitude}
          longitude={listing.longitude}
        />

        {listing.description && (
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Description
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="text-listing-description">
              {listing.description}
            </p>
          </div>
        )}

        {listing.dealNotes && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-6">
            <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-600" />
              Deal Notes
            </h3>
            <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-line" data-testid="text-deal-notes">
              {listing.dealNotes}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Building className="h-4 w-4 text-gray-400" />
              Property Facts
            </h3>
            <DetailRow label="Bedrooms" value={listing.bedrooms} />
            <DetailRow label="Bathrooms" value={listing.bathrooms} />
            <DetailRow label="Square Footage" value={listing.sqft ? `${listing.sqft.toLocaleString()} sqft` : null} />
            <DetailRow label="Lot Size" value={listing.lotSize ? `${listing.lotSize} acres` : null} />
            <DetailRow label="Year Built" value={listing.yearBuilt} />
            <DetailRow label="Estimated Days Available" value={listing.estimatedDaysAvailable} />
          </div>

          <div className="bg-white border border-gray-100 rounded-xl p-5" data-tour="wholesaler-listing-detail-financials">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-400" />
              Financials
            </h3>
            <DetailRow label="Asking Price" value={formatCurrency(listing.askingPrice)} />
            <DetailRow label="ARV (After Repair Value)" value={formatCurrency(listing.arv)} />
            <DetailRow label="Seller Financing" value={listing.sellerFinancing ? "Available" : null} />
            <DetailRow label="Interest Rate" value={formatPercent(listing.interestRate)} />
            <DetailRow label="Term (Years)" value={listing.termYears} />
            <DetailRow label="Down Payment" value={formatPercent(listing.downPaymentPercent)} />
          </div>
        </div>

        {(listing.partnerName || listing.partnerCompanyName) && (
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-8">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Tag className="h-4 w-4 text-amber-500" />
              Listing Wholesaler
            </h3>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-base font-bold">
                {initials}
              </div>
              <div className="flex-1">
                {listing.partnerName && (
                  <p className="font-semibold text-gray-900" data-testid="text-partner-name">{listing.partnerName}</p>
                )}
                {listing.partnerCompanyName && (
                  <p className="text-sm text-gray-500" data-testid="text-partner-company">{listing.partnerCompanyName}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 flex-wrap">
                  {listing.partnerEmail && (
                    <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{listing.partnerEmail}</span>
                  )}
                  {listing.partnerPhone && (
                    <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{listing.partnerPhone}</span>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInterest(true)}
                data-testid="button-express-interest-partner"
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                Contact
              </Button>
            </div>
          </div>
        )}

        <div className="border-t border-gray-100 pt-4 mb-8">
          <p className="text-xs text-gray-400 leading-relaxed" data-testid="text-disclaimer">
            * All property details, photos, and financial information shown on this page are provided by the listing wholesaler and have not been independently verified by PadPulse. Buyers should conduct their own due diligence and consult qualified professionals before making any purchase decision.
          </p>
        </div>
      </div>

      <Dialog open={showInterest} onOpenChange={(open) => { if (!open) handleCloseInterest(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{interestSent ? "Interest Sent!" : "Express Interest"}</DialogTitle>
            <DialogDescription>{listing.address} · {formatCurrency(listing.askingPrice)}</DialogDescription>
          </DialogHeader>
          {interestSent ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm text-slate-700 font-medium">Your interest has been sent to the wholesaler.</p>
              <p className="text-xs text-slate-500">You'll receive a message in your inbox when they respond.</p>
              <Button className="mt-2" onClick={handleCloseInterest} data-testid="button-close-interest">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="interest-msg">Message to wholesaler (optional)</Label>
                <Textarea
                  id="interest-msg"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Tell the wholesaler why you're interested, your timeline, or any questions..."
                  data-testid="input-interest-message"
                />
              </div>
              <Button
                className="w-full"
                onClick={() => interestMutation.mutate(message)}
                disabled={interestMutation.isPending}
                data-testid="button-send-interest"
              >
                {interestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send Interest
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-5xl p-0 bg-black border-0 overflow-hidden"
          data-testid="lightbox"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{listing.address} photos</DialogTitle>
            <DialogDescription>Photo {safeIdx + 1} of {photos.length}</DialogDescription>
          </DialogHeader>
          <div className="relative w-full bg-black flex items-center justify-center" style={{ minHeight: "60vh" }}>
            {hasPhotos && (
              <img
                src={photos[safeIdx]}
                alt={`${listing.address} photo ${safeIdx + 1}`}
                className="max-h-[80vh] max-w-full object-contain"
                data-testid="lightbox-image"
              />
            )}
            {hasMany && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Previous photo"
                  data-testid="button-lightbox-prev"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-900" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Next photo"
                  data-testid="button-lightbox-next"
                >
                  <ChevronRight className="h-5 w-5 text-gray-900" />
                </button>
              </>
            )}
            <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-medium rounded-full px-3 py-1">
              {safeIdx + 1} / {photos.length}
            </div>
          </div>
          {hasMany && (
            <div className="flex gap-2 p-3 bg-black/95 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={`lb-${p}-${i}`}
                  type="button"
                  onClick={() => setPhotoIdx(i)}
                  className={`flex-shrink-0 h-14 w-20 rounded overflow-hidden border-2 transition-all ${
                    i === safeIdx ? "border-amber-400" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Show photo ${i + 1}`}
                  data-testid={`button-lightbox-thumb-${i}`}
                >
                  <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
